/**
 * Formatea un número para mostrar en la interfaz de usuario con separadores de miles
 * Específico para moneda guaraní: sin decimales, solo enteros
 * 
 * @param {number|string} value - El valor numérico a formatear (350000.00 → "350.000")
 * @returns {string} Número formateado para mostrar (ej: "350.000")
 */
export const formatNumberForDisplay = (value) => {
	// Si es undefined, null o string vacío, devolver string vacío
	if (value === undefined || value === null || value === '') {
		return '';
	}

	// Convertir a número
	let numValue;

	if (typeof value === 'string') {
		// Si es string, primero eliminar los separadores de miles si los tiene
		// Luego convertir a número
		numValue = parseFloat(Math.floor(value).toString().replace(/\./g, '').replace(',', '.'));
	} else {
		numValue = parseFloat(Math.floor(value));
	}

	// Si no es un número válido después de la conversión, devolver string vacío
	if (isNaN(numValue)) {
		return '';
	}

	// Convertir a entero (eliminar decimales)
	numValue = Math.floor(numValue);

	// Formatear el número con puntos como separadores de miles
	return new Intl.NumberFormat('es-PY', {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format(numValue);
};

/**
 * Convierte un valor formateado para UI a un formato válido para el servidor
 * 
 * @param {string} displayValue - El valor mostrado en la UI (ej: "350.000")
 * @returns {number|null} Valor numérico entero para enviar al servidor (ej: 350000)
 */
export const parseNumberForServer = (displayValue) => {
	// Si es undefined, null o string vacío, devolver null
	if (displayValue === undefined || displayValue === null || displayValue === '') {
		return null;
	}

	// Si ya es un número, asegurar que sea entero
	if (typeof displayValue === 'number') {
		return Math.floor(displayValue);
	}

	// Eliminar todos los separadores de miles (puntos)
	const normalized = displayValue.toString().replace(/\./g, '');

	// Convertir a número entero
	const numValue = parseInt(normalized, 10);
	return isNaN(numValue) ? null : numValue;
};

/**
 * Formatea un valor como precio en guaraníes para mostrar en UI
 * Incluye el prefijo "Gs." y separadores de miles
 * 
 * @param {number|string} value - El valor numérico (350000 → "Gs. 350.000")
 * @param {string} defaultValue - Valor a mostrar si es null/undefined (default: "Gs. 0")
 * @returns {string} Precio formateado (ej: "Gs. 350.000")
 */
export const formatPrice = (value, defaultValue = 'Gs. 0') => {
	if (value === undefined || value === null || value === '') {
		return defaultValue;
	}

	const formatted = formatNumberForDisplay(value);
	return formatted ? `Gs. ${formatted}` : defaultValue;
};

/**
 * Formatea un valor como porcentaje
 * 
 * @param {number} value - El valor numérico (0.25 → "25%" o 25 → "25%")
 * @param {boolean} isDecimal - Si true, multiplica por 100 (0.25 → 25%)
 * @param {number} decimals - Cantidad de decimales a mostrar
 * @returns {string} Porcentaje formateado
 */
export const formatPercentage = (value, isDecimal = false, decimals = 0) => {
	if (value === undefined || value === null || value === '') {
		return '0%';
	}

	let numValue = parseFloat(value);
	if (isNaN(numValue)) {
		return '0%';
	}

	if (isDecimal) {
		numValue = numValue * 100;
	}

	return `${numValue.toFixed(decimals)}%`;
};

/**
 * Formatea un número con decimales (útil para cantidades, pesos, etc.)
 * 
 * @param {number|string} value - El valor numérico
 * @param {number} decimals - Cantidad de decimales (default: 2)
 * @returns {string} Número formateado con decimales
 */
export const formatDecimal = (value, decimals = 2) => {
	if (value === undefined || value === null || value === '') {
		return '';
	}

	const numValue = parseFloat(value);
	if (isNaN(numValue)) {
		return '';
	}

	return new Intl.NumberFormat('es-PY', {
		style: 'decimal',
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals
	}).format(numValue);
};